// use client: FAQ category/accordion selection state and scrollToElement
'use client'

/* guides.css first: this page reuses guides UI (category icons, topic index)
   that the old SPA styled via the global bundle; business.css stays after it
   so its page-specific overrides keep winning ties, as they did in prod. */
import '@/views/personal/guides/guides.css'
import '../business.css'

import { useState } from 'react'
import { Col, Offcanvas } from 'react-bootstrap'
import { isEmpty } from 'lodash'

import MenuIcon from '@/assets/images/burger_icon.svg'
import BG from '@/assets/images/hero-images/BG.webp'
import Wealth from '@/assets/images/hero-images/wealth.webp'
import AccordionSimple from '@/components/accordion/AccordionSimple'
import { Jumbotron } from '@/sections'
import LetsConnect from '@/sections/ComingSoon/LetsConnect'
import { formatTitleWithItalic, generateSlug } from '@/utils'
import { cmsImageSrc } from '@/utils/cmsImageSrc'

function FAQHelpAndSupport({ initialData }) {
  // Content comes from the build-time snapshot only. A Strapi publish rebuilds
  // and redeploys the site, so there is nothing to revalidate on mount. The
  // removed effect was nominally search-driven, but `search` had no setter — the
  // search UI was never wired, so it only ever ran once on mount with an empty
  // query. See tests/no-client-cms-fetch.spec.ts.
  const faqType = initialData?.FaqType ?? []
  const [activeFaqType, setActiveFaqType] = useState(initialData?.FaqType?.[0]?.title)
  const [faqList, setFaqList] = useState(initialData?.FaqType?.[0]?.FaqParent ?? [])
  // Seeded with the first answer's slug, which the removed fetch used to set.
  const [activeItem, setActiveItem] = useState(
    generateSlug(initialData?.FaqType?.[0]?.FaqParent?.[0]?.title ?? '')
  )
  const [isMenu, setIsMenu] = useState(false)

  const scrollToElement = (_, hash) => {
    const element = document.getElementById(hash)
    setActiveItem(hash)
    const offset = 265

    if (element) {
      const topPosition = element.getBoundingClientRect().top + window.pageYOffset - offset
      window.scrollTo({ top: topPosition, behavior: 'smooth' })
    }
  }

  return (
    <div className="faq-help-and-support">
      <section className="section jumbotron">
        <Jumbotron
          badgeText="FAQ"
          title={`
              <span className="jumbotron-subtitle-2">
                Questions?
              </span><br />
              We got answers!
              `}
          buttonText="Reach out to us!"
          backgroundImage={Wealth.src}
          uniqueBg={BG.src}
        />

        {/* Category Icons */}
        <div className="guide-category-container overflow-hidden" style={{ border: 'none' }}>
          {faqType &&
            faqType?.map((item, index) => (
              <Col
                className="d-flex flex-column align-items-center"
                data-aos="fade-up"
                data-aos-delay={`${index}00`}
                key={item.title}
                style={{
                  width: '100px',
                  flex: 'unset',
                  color: 'var(--grey-500-body-text)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  margin: '0 8px'
                }}
                onClick={e => {
                  setActiveFaqType(item?.title)
                  setFaqList(item?.FaqParent)
                  if (item?.FaqParent[0]?.title) {
                    scrollToElement(e, generateSlug(item?.FaqParent[0]?.title))
                  }
                }}
              >
                <div className="guides-icon-container mb-4">
                  <img
                    src={cmsImageSrc(item?.icon?.image || item?.icon?.imageLink)}
                    alt="Type Icon"
                    className="d-block"
                    width="100%"
                  />
                </div>
                <div
                  style={{
                    borderBottom:
                      activeFaqType === item.title
                        ? `2px solid var(--alrajhi-primary-600)`
                        : undefined,
                    transition: 'all 0.3s ease-in'
                  }}
                >
                  <h6
                    style={{
                      fontFamily: activeFaqType === item.title ? 'var(--font-ping)' : undefined,
                      fontWeight: activeFaqType === item.title ? 700 : undefined,
                      color: activeFaqType === item.title ? '#272424' : undefined,
                      transition: 'all 0.3s ease-in'
                    }}
                  >
                    {formatTitleWithItalic(item?.title)}
                  </h6>
                </div>
              </Col>
            ))}
        </div>

        {/* Index for Mobile Screen */}
        <section className="d-block d-lg-none py-3 float-mobile-index">
          <div className="article-topic-index-mobile mx-auto">
            <ul>
              {faqList?.map(
                item =>
                  activeItem === generateSlug(item.title) && (
                    <li
                      key={item.title}
                      onClick={e => scrollToElement(e, generateSlug(item.title))}
                      className={activeItem === generateSlug(item.title) ? 'active' : ''}
                    >
                      {item.title}
                    </li>
                  )
              )}
            </ul>
            <div
              className={`menu-icon ${isMenu ? 'active' : ''}`}
              onClick={() => setIsMenu(!isMenu)}
            >
              <img src={MenuIcon.src} alt="Menu Icon" />
            </div>
          </div>
        </section>

        <section
          className="section about container"
          data-aos="fade-up"
          data-aos-delay="200"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '50px'
          }}
        >
          <div className="col-lg-9">
            {!isEmpty(faqList) ? (
              faqList?.map(item => (
                <div key={item.title} className="mb-5">
                  <h3 className="heading mb-5 fw-bold" id={generateSlug(item.title)}>
                    {formatTitleWithItalic(item?.title)}
                  </h3>
                  <AccordionSimple data={item?.FaqData} />
                </div>
              ))
            ) : (
              <div className="text-center"> No data available</div>
            )}
          </div>
          {!isEmpty(faqList) && (
            <Col className="d-none d-lg-block">
              <div className="article-topic-index overflow-hidden">
                <ul>
                  {faqList?.map(item => (
                    <li
                      key={item.title}
                      onClick={e => scrollToElement(e, generateSlug(item.title))}
                      className={`topic-list-item ${
                        activeItem === generateSlug(item.title) ? 'active' : ''
                      }`}
                    >
                      {formatTitleWithItalic(item.title)}
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          )}
        </section>
      </section>

      <LetsConnect />

      <Offcanvas
        show={isMenu}
        onHide={() => setIsMenu(false)}
        // centered
        // size="lg"
        className="custom-modal-content"
        placement="bottom" // key bit: bottom placement
        scroll={false}
        backdrop
      >
        <Offcanvas.Header closeButton style={closeButtonStyle} className="custom-close-button">
          <Offcanvas.Title>{activeFaqType}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="ping text-grey-color p-0 overflow-hidden" style={headerStyle}>
          <div className="d-flex flex-column">
            {!isEmpty(faqList) && (
              <Col className="">
                <div className="article-topic-index">
                  <ul>
                    {faqList?.map(item => (
                      <li
                        key={item.title}
                        onClick={e => {
                          scrollToElement(e, generateSlug(item.title))
                          setIsMenu(false)
                        }}
                        className={`topic-list-item ${
                          activeItem === generateSlug(item.title) ? 'mobile-active' : ''
                        }`}
                      >
                        {formatTitleWithItalic(item.title)}
                      </li>
                    ))}
                  </ul>
                </div>
              </Col>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  )
}

export default FAQHelpAndSupport

const headerStyle = {
  // borderRadius: "20px 20px 0 0",
  // backgroundColor: "#fff",
}

const closeButtonStyle = {
  // position: "absolute",
  // zIndex: 2,
  // right: 0,
  // padding: "18px",
}
